import * as React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { DataCollectionData } from 'benchmarking-domain/data-collection';
import { DataPointValue } from 'benchmarking-domain/data-point';
import ProjectSection from 'benchmarking-domain/project-section';
import { QuestionnaireSectionRow } from 'benchmarking-domain/questionnaire';

import { DeleteDynamicItemButton } from './dynamic-questionaire-delete-button';
import { QuestionnaireSectionTable } from './questionnaire-section-table';

const QuestionnaireSectionTableMemo = React.memo(QuestionnaireSectionTable);

export interface QuestionnaireSectionTableProps {
  name: string;
  itemTypeName: string;
  expanded?: boolean;
  rows: QuestionnaireSectionRow[];
  dataCollection?: DataCollectionData;
  dataCollectionChangeHandler: (valueId: string, value: DataPointValue) => Promise<void>;
  globalVariables: DataCollectionData;
  projectSection: ProjectSection;
  onDelete: (data: ProjectSection) => Promise<void>;
  onExpandChange: (_: React.SyntheticEvent, expanded: boolean) => void;
}

export function DynamicQuestionnaireAccordion({
  name,
  itemTypeName,
  rows,
  expanded,
  dataCollection,
  dataCollectionChangeHandler,
  globalVariables,
  projectSection,
  onDelete,
  onExpandChange,
}: QuestionnaireSectionTableProps): React.JSX.Element {
  const ref = React.useRef<HTMLDivElement>(null);

  const handleAccordionChange = (_: React.SyntheticEvent, e: boolean) => {
    onExpandChange(_, e);

    if (e) {
      const element = ref?.current?.previousElementSibling ?? ref?.current;

      setTimeout(() => {
        if (element) {
          const rect = element.getBoundingClientRect();
          const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

          if (!isVisible) {
            element?.scrollIntoView();
            window.scrollBy({ top: -100 });
          }
        }
      }, 50);
    }
  };

  return (
    <Accordion expanded={expanded} onChange={handleAccordionChange} slotProps={{ transition: { timeout: 0 } }}>
      <AccordionSummary ref={ref} expandIcon={<ExpandMoreIcon />} sx={{ justifyContent: 'space-between' }}>
        <Stack direction={'row'} justifyContent={'space-between'} width={'100%'}>
          <Typography variant="body1">
            {itemTypeName}: {name}
          </Typography>
          <DeleteDynamicItemButton dynamicItemName={name} projectSection={projectSection} onDelete={onDelete} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {expanded && (
          <Stack spacing={3}>
            <Card>
              <QuestionnaireSectionTableMemo
                rows={rows}
                valueIdPrefix={name}
                dataCollection={dataCollection}
                dataCollectionChangeHandler={dataCollectionChangeHandler}
                globalVariables={globalVariables}
              />
            </Card>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
